import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FilterWrapper, ValueFormatterContext } from 'shared/ui/filter/filter-wrapper';
import { ManagerUrlParamsFilter } from 'entities/filters/manager-url-params-filter';
import { DateUrlParamsFilter } from 'entities/filters/date-url-params-filter';
import { TaskStatusParamFilter } from './tasks-status-filter';
import { SubordinatesParamsFilter } from 'entities/filters/subordinates-params-filter';
import { useManagersDropdownList } from 'features/user/user-picker/ui';
import { useReadOnlyRemoteCollection } from 'utils/remote-collection.hook';
import { fetchSubordinatesDropdown } from 'features/user/user-picker/employee-picker';
import { IManager } from '@sr/dto';
import { TASK_STATUS_MAP } from 'shared/ui/formik-fields/task-status-picker-field';
import { Stack } from '@mui/material';

export const TasksFilter = () => {
  const [searchParams] = useSearchParams();
  const managerIdParam = searchParams.get('managerId');
  const managerId = managerIdParam ? Number(managerIdParam) : null;
  const { items: managers } = useManagersDropdownList();
  const { items: employees } = useReadOnlyRemoteCollection<IManager>({
    fetchHandler: useCallback(() => fetchSubordinatesDropdown(managerId), [managerId]),
  });

  const managerValueFormatter = ({ value }: ValueFormatterContext) => {
    const manager = managers.find((m) => String(m.id) === value);
    return manager ? `${manager.firstName} ${manager.secondName || ''}`.trim() : value;
  };

  const employeeValueFormatter = ({ value }: ValueFormatterContext) => {
    const employee = employees.find((e) => String(e.id) === value);
    return employee ? `${employee.firstName} ${employee.secondName || ''}`.trim() : value;
  };

  const statusValueFormatter = ({ value }: ValueFormatterContext) => TASK_STATUS_MAP[Number(value)] ?? value;

  return (
    <FilterWrapper title="Фильтры задач" dense>
      <FilterWrapper.Item label="Менеджер" queryKeys="managerId" valueFormatter={managerValueFormatter}>
        <ManagerUrlParamsFilter queryParamName="managerId" size="small" />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Сотрудник" queryKeys="employeeId" valueFormatter={employeeValueFormatter}>
        <SubordinatesParamsFilter queryParamName="employeeId" size="small" managerId={managerId} />
      </FilterWrapper.Item>

      <FilterWrapper.Item label="Статус" queryKeys="status" valueFormatter={statusValueFormatter}>
        <TaskStatusParamFilter queryParamName="status" label="Статус" size="small" />
      </FilterWrapper.Item>

      <FilterWrapper.Item
        label="Срок"
        queryKeys={['fromDueDate', 'toDueDate']}
        summaryFormatter={({ searchParams }) => {
          const fromDate = searchParams.get('fromDueDate');
          const toDate = searchParams.get('toDueDate');
          if (!fromDate && !toDate) return 'Срок';
          if (fromDate && toDate) return `Срок: ${fromDate} — ${toDate}`;
          return `Срок: ${fromDate || toDate}`;
        }}
      >
        <Stack spacing={1.5}>
          <DateUrlParamsFilter queryParamName="fromDueDate" label="Срок с" size="small" />
          <DateUrlParamsFilter queryParamName="toDueDate" label="Срок по" size="small" />
        </Stack>
      </FilterWrapper.Item>
    </FilterWrapper>
  );
};
